import { useCallback, useMemo } from 'react';

import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import useQueryParams from '@/hooks/useQueryParams';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import { SimpleErrorResponse } from '@/models/Error';
import { buildCacheInvocationListPath } from '@/utils/monolithApiRouteService';
import { updateQueryString } from '@/utils/queryParams';

enum PageQueryParamKeys {
  page = 'invocations_page',
  perPage = 'invocations_items_per_page',
  sortBy = 'invocations_order_by',
  sortDirection = 'invocations_order_direction',
}

enum QueryParamKeys {
  page = 'page',
  perPage = 'items_per_page',
  sortBy = 'order_by',
  sortDirection = 'order_direction',
  buildSlug = 'build_slug',
  tool = 'tool',
}

type InvocationListResponse = {
  items: BuildToolInvocationWithDetails[];
  paging: {
    page: number;
    perPage: number;
    totalCount: number;
  };
};

const useInvocations = (workspaceSlug: string, buildSlug: string, tool: BuildCacheToolIds) => {
  const queryParams = useQueryParams();

  const searchParams = useMemo(() => {
    const page = queryParams.get(PageQueryParamKeys.page);
    const perPage = queryParams.get(PageQueryParamKeys.perPage);
    const sortBy = queryParams.get(PageQueryParamKeys.sortBy);
    const sortDirection = queryParams.get(PageQueryParamKeys.sortDirection);

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append(QueryParamKeys.buildSlug, buildSlug);
    urlSearchParams.append(QueryParamKeys.tool, tool || BuildCacheToolIds.gradle);

    if (page) {
      urlSearchParams.append(QueryParamKeys.page, page);
    }

    if (perPage) {
      urlSearchParams.append(QueryParamKeys.perPage, perPage);
    }

    if (sortBy) {
      urlSearchParams.append(QueryParamKeys.sortBy, sortBy);
    }

    if (sortDirection) {
      urlSearchParams.append(QueryParamKeys.sortDirection, sortDirection);
    }

    return urlSearchParams.toString();
  }, [queryParams, buildSlug, tool]);

  const url = useMemo(() => {
    return buildCacheInvocationListPath(workspaceSlug, { format: 'json', params: searchParams });
  }, [searchParams, workspaceSlug]);

  const onPageChange = useCallback(
    (newPage: number) => {
      queryParams.set(PageQueryParamKeys.page, newPage.toString());
      updateQueryString(queryParams);
    },
    [queryParams],
  );

  const onPerPageChange = useCallback(
    (newPerPage: number) => {
      queryParams.set(PageQueryParamKeys.perPage, newPerPage.toString());
      queryParams.set(PageQueryParamKeys.page, '');
      updateQueryString(queryParams);
    },
    [queryParams],
  );

  return {
    ...useMonolithApiQuery<InvocationListResponse, SimpleErrorResponse>(['buildCacheBuildInvocationList', url], url),
    onPageChange,
    onPerPageChange,
  };
};

export default useInvocations;
